import React, { useRef, useState, useCallback, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

export const Gallery = ({ images = [], testid = "gallery" }) => {
  const { t } = useLang();
  const trackRef = useRef(null);
  const [lightbox, setLightbox] = useState(null); // index or null

  const scrollBy = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const amount = track.clientWidth * 0.8 * dir;
    track.scrollBy({ left: amount, behavior: "smooth" });
  }, []);

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const step = useCallback((dir) => {
    setLightbox((prev) => {
      if (prev === null) return prev;
      const next = (prev + dir + images.length) % images.length;
      return next;
    });
  }, [images.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, step]);

  return (
    <div className="relative" data-testid={testid}>
      <div
        ref={trackRef}
        className="flex gap-4 md:gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-2"
      >
        {images.map((src, i) => (
          <button
            key={i}
            data-testid={`${testid}-item-${i}`}
            onClick={() => openLightbox(i)}
            className="group relative shrink-0 snap-start w-[85%] sm:w-[60%] lg:w-[46%] aspect-[4/3] overflow-hidden bg-sand"
          >
            <img
              src={src}
              alt={`${testid} ${i + 1}`}
              loading={i < 6 ? "eager" : "lazy"}
              className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-500 flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button
          data-testid={`${testid}-prev`}
          onClick={() => scrollBy(-1)}
          aria-label={t.common.prev}
          className="w-12 h-12 border border-ink/20 flex items-center justify-center hover:bg-ink hover:text-white transition-colors duration-300"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          data-testid={`${testid}-next`}
          onClick={() => scrollBy(1)}
          aria-label={t.common.next}
          className="w-12 h-12 border border-ink/20 flex items-center justify-center hover:bg-ink hover:text-white transition-colors duration-300"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="brass-line flex-1 ml-2" />
      </div>

      {lightbox !== null && (
        <div
          data-testid={`${testid}-lightbox`}
          className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          onClick={closeLightbox}
        >
          <button
            data-testid={`${testid}-lightbox-close`}
            onClick={closeLightbox}
            aria-label={t.common.close}
            className="absolute top-5 right-5 text-white/80 hover:text-brass transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            aria-label={t.common.prev}
            className="absolute left-3 sm:left-8 text-white/70 hover:text-brass transition-colors"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <img
            src={images[lightbox]}
            alt={`enlarged ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full object-contain shadow-2xl"
          />
          <button
            onClick={(e) => { e.stopPropagation(); step(1); }}
            aria-label={t.common.next}
            className="absolute right-3 sm:right-8 text-white/70 hover:text-brass transition-colors"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm tracking-widest">
            {lightbox + 1} / {images.length}
          </span>
        </div>
      )}
    </div>
  );
};
